import React from "react";
import { Grid, Link, makeStyles } from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import { darken } from "@material-ui/core/styles/colorManipulator";
import OverflowTooltip from "../../../../components/OverflowTooltip/OverflowTooltip";
import { ReactComponent as UploadedFileIcon } from "../../../../assets/svg/uploaded-file.svg";
import theme from "../../../../themes";
import { REQUIRED_DOCS } from "../../constants";
import { IViewAcademicWizardProps } from "../../types";
import { getFileSize } from "../helpers/helperFunctions";

const useStyles = makeStyles(() => ({
  card: {
    backgroundColor: "#f5f6f7",
    borderRadius: 12,
    padding: 16,
    color: "#000",
    fontWeight: "normal",
    fontSize: 16,
    "& + &": {
      marginTop: 16,
    },
  },
  label: {
    color: "#383d4a",
    fontWeight: "bold",
    fontSize: 18,
    marginBottom: 8,
  },
  docsContainer: {
    marginLeft: 16,
  },
  docGridItem: {
    position: "relative",
    "&::before": {
      content: '""',
      width: 12,
      height: 12,
      backgroundColor: "#0676ed",
      borderRadius: 7,
      position: "absolute",
      bottom: 9,
      left: -16,
    },
  },
  applicationFormContainer: {
    marginTop: 16,
    display: "flex",
    alignItems: "center",
  },
  applicationFormInfo: {
    textAlign: "left",
    marginLeft: 24,
    "&>p:first-child": {
      color: "#535358",
      fontSize: 14,
      fontWeight: "bold",
    },
    "&>p:last-child": {
      marginTop: -2,
      fontSize: 10,
      color: "#77838f",
    },
  },
  doc: {
    width: 86,
  },
  link: {
    fontWeight: "bold",
    color: theme.palette.info.main,
    "&:hover": {
      color: darken(theme.palette.info.main, 0.2),
      cursor: "pointer",
    },
    textDecoration: "none",
  },
}));

export const ViewAcademic = ({
  degreeName,
  schoolLevelName,
  schoolGrade,
  schoolTypeName,
  applicationLink,
  applicationForms = [],
  requiredDocuments = [],
}: IViewAcademicWizardProps) => {
  const classes = useStyles();

  const requiredDocNames = requiredDocuments.map((doc) => {
    const existedDoc = REQUIRED_DOCS.find((d) => d.name === doc.name);

    return existedDoc ? existedDoc.longName : doc.name;
  });

  if (applicationLink && !applicationLink.includes("http", 0)) {
    applicationLink = "http://" + applicationLink;
  }

  return (
    <>
      {degreeName && (
        <div className={classes.card}>
          <div className={classes.label}>Degree</div>
          {degreeName}
        </div>
      )}
      {schoolLevelName && (
        <div className={classes.card}>
          <div className={classes.label}>School Level</div>
          {schoolLevelName}
        </div>
      )}
      {schoolGrade && (
        <div className={classes.card}>
          <div className={classes.label}>School Grade</div>
          <OverflowTooltip text={schoolGrade} />
        </div>
      )}
      {schoolTypeName && (
        <div className={classes.card}>
          <div className={classes.label}>School Type</div>
          {schoolTypeName}
        </div>
      )}
      {!!requiredDocNames.length && (
        <div className={classes.card}>
          <div className={classes.label}>Required Documents</div>
          <Grid container spacing={1} className={classes.docsContainer}>
            {requiredDocNames.map((doc) => (
              <Grid key={doc} item xs={3} className={classes.docGridItem}>
                <div className={classes.doc}>
                  <OverflowTooltip text={doc} />
                </div>
              </Grid>
            ))}
          </Grid>
        </div>
      )}

      {!!applicationForms.length && (
        <div className={classes.card}>
          <div className={classes.label}>Application File</div>
          {applicationForms.map((applicationForm) => (
            <div
              key={applicationForm.fileId}
              className={classes.applicationFormContainer}
            >
              <UploadedFileIcon />
              <div className={classes.applicationFormInfo}>
                <Typography>
                  <Link
                    className={classes.link}
                    href={applicationForm.path}
                    target="_blank"
                    underline="none"
                  >
                    <OverflowTooltip text={applicationForm.name} />
                  </Link>
                </Typography>
                <Typography>
                  File size: {getFileSize(applicationForm.size)}
                </Typography>
              </div>
            </div>
          ))}
        </div>
      )}
      {applicationLink && (
        <div className={classes.card}>
          <div className={classes.label}>Application Link</div>
          <a className="wedu-link" href={applicationLink} target="_blank">
            See the link
          </a>
        </div>
      )}
    </>
  );
};
